/**
 * The wall of country at the map's edge (ROUND4 item 6). The simulation already stops the ranger at
 * the edge of the playable rectangle; until now nothing on screen said so, and the apron of plain
 * ground beyond it invited him to keep walking. One crest piece per biome, repeated along the
 * boundary by `world/mapEdge`: a treeline, a pressure ridge, an escarpment.
 *
 * Every piece is modelled about its foot with +z pointing INWARD, toward the map, and is laid
 * without any yaw of its own - so how far it reaches toward the playable ground is exactly its
 * own `boundingBox.max.z`, which is what `mapEdge` measures to keep the whole band outside.
 */

import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';

const TAU = Math.PI * 2;

/** Temperate: a bank of scrub with a close-grown stand of conifers out of it - no way through, no way round. */
function treeLine(p: Palette, rng: Pcg32): MeshBuilder {
  const mb = new MeshBuilder();
  mb.box(3.4, rng.range(0.5, 0.85), 1.4, { all: p.color('foliage.lo'), top: p.color('foliage.mid') });
  for (const x of [-0.88, 0, 0.88]) {
    const r = rng.range(0.55, 0.72);
    const at = place(x + rng.range(-0.08, 0.08), rng.range(0.35, 0.6), rng.range(-0.1, 0.1), rng.range(0, TAU));
    mb.cylinder(r, 0, rng.range(2.1, 3.4), 6, p.color(rng.chance(0.5) ? 'foliage.lo' : 'foliage.mid'), at);
  }
  return mb;
}

/** Arctic: a pressure ridge - slabs of ice shoved up out of the snow and capped with drift. */
function iceRidge(p: Palette, rng: Pcg32): MeshBuilder {
  const mb = new MeshBuilder();
  mb.box(3.4, rng.range(0.45, 0.7), 1.4, { all: p.color('ground.2'), top: p.color('ground.3') });
  for (const x of [-1.0, 0.05, 1.05]) {
    const slab = { all: p.color('water.2'), top: p.color('ground.3'), front: p.color('water.1') };
    const roll = rng.range(0.16, 0.34) * (x < 0 ? -1 : 1);
    const at = place(x, rng.range(0.3, 0.5), rng.range(-0.12, 0.12), 0, 1, 0, roll);
    mb.box(rng.range(0.8, 1.1), rng.range(1.5, 2.5), rng.range(0.45, 0.7), slab, at);
  }
  return mb;
}

/** Desert: the lip of an escarpment - stepped blocks of mesa rock, too steep and too loose to climb. */
function escarpment(p: Palette, rng: Pcg32): MeshBuilder {
  const mb = new MeshBuilder();
  mb.box(3.4, rng.range(0.55, 0.9), 1.4, { all: p.color('rock.lo'), top: p.color('rock.mid') });
  for (const x of [-1.0, 0.1, 1.0]) {
    const block = { all: p.color('rock.mid'), top: p.color('rock.hi'), front: p.color('rock.lo') };
    const at = place(x, rng.range(0.4, 0.7), rng.range(-0.1, 0.1), 0, 1, 0, rng.range(-0.12, 0.12));
    mb.box(rng.range(0.9, 1.3), rng.range(1.4, 2.4), rng.range(0.5, 0.75), block, at);
  }
  return mb;
}

/** One piece of the band, in the active biome's own language. */
export const edgeCrest: KitBuilder = (p, rng) => {
  if (p.biome === 'arctic') return iceRidge(p, rng);
  if (p.biome === 'desert') return escarpment(p, rng);
  return treeLine(p, rng);
};
